import { get, isEmpty, remove, startCase } from 'lodash/fp';

import {
  GroupBy,
  Metric,
} from 'dashboard/components/WorkflowControlPanel/StepConfigAction/GetAnalyticsMetricV1';
import {
  getHandlebars,
  isValidHandlebarsText,
  validateResolvedJson,
} from 'dashboard/lib/utils/handlebars';
import { RuleValueTypes } from 'dashboard/lib/workflows';
import {
  getExpressionRuleParts,
  SINGLE_OPERAND_OPERATORS,
  VARIABLE_TYPE_TO_OPERATORS,
} from 'dashboard/lib/workflows/expression';
import {
  caseAttachmentsFor,
  filterUsableObjects,
  flattenKeyValue,
  Operator,
  taggableObjectsFor,
  UsableObjectConfig,
} from 'dashboard/lib/workflows/objects';
import {
  fieldPathHasValidSchema,
  WorkflowValidationContext,
} from 'dashboard/lib/workflows/validation/validationContext';
import { BaseTrigger } from 'dashboard/models/trigger';
import {
  WorkflowConfigError,
  WorkflowErrorType,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import {
  ActionFor,
  DeprecatedWorkflowStepActionKey,
  NullWorkflowStepActionKey,
  StaticWorkflowAction,
  WorkflowActionRunGraphQueryV1,
  WorkflowActionSyncGraphV1,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { EVENT_NAME_TO_EVENT_TYPE, EventName, EventType } from 'lib/constants/events';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchema } from 'lib/json-schema';
import { findPath } from 'lib/workflows';

type PartialWorkflowError = Pick<WorkflowConfigError, 'message' | 'field'>;
const Valid = 'valid' as const;
type ValidationResult = PartialWorkflowError | PartialWorkflowError[] | typeof Valid;
export type StepContext = {
  usableObjects: UsableObjectConfig[];
  jsonSchema: JsonSchema;
};

type ActionParamsValidators = {
  [P in StaticWorkflowAction['key']]: ((given: {
    params: ActionFor<P>['params'];
    context: WorkflowValidationContext;
    stepContext: StepContext;
    step: WorkflowStepAction;
  }) => ValidationResult)[];
};

// Valid if the field is truthly or a non-empty array
const validatePresenceOf =
  (path: string, message = `${startCase(path)} should be selected`, erroringField = path) =>
  ({ params }: { params: StaticWorkflowAction['params'] }): ValidationResult => {
    const value = get(path, params);

    let isPresent = !!value;
    if (Array.isArray(value) && !!value.length) {
      isPresent = !!value.length;
    } else if (value && typeof value == 'object') {
      isPresent = !!Object.keys(value).length;
    }

    return isPresent
      ? Valid
      : {
          field: erroringField,
          message,
        };
  };

// Validate the text field contains valid handlebar text and each variable can be substituted.
// Note that an empty string will pass this validation.
const validateHandlebarsOf =
  (path: string, fieldName = startCase(path)) =>
  ({
    params,
    context,
    stepContext,
  }: {
    params: StaticWorkflowAction['params'];
    context: WorkflowValidationContext;
    stepContext: StepContext;
  }): ValidationResult => {
    const param = get(path, params);

    if (!isValidHandlebarsText(param)) {
      return {
        field: path,
        message: `${fieldName} is invalid as text with Handlebars`,
      };
    }

    const errorMessages = getHandlebars(param).flatMap(
      (handlebar) => fieldPathHasValidSchema(handlebar, stepContext, context).errorMessages,
    );
    return errorMessages.length > 0
      ? {
          field: path,
          message: `${fieldName} has invalid handlebars: ${errorMessages.join(', ')}`,
        }
      : Valid;
  };

// Validate the string is a valid JSON, allowing handlebar mustaches.
const validateResolvedJsonOf =
  (path: string, message = `${startCase(path)} is invalid JSON`) =>
  ({ params }: { params: StaticWorkflowAction['params'] }): ValidationResult => {
    const value = get(path, params);
    return value.trim() && !validateResolvedJson(value.trim())
      ? {
          message: message,
          field: path,
        }
      : Valid;
  };

// Validate a string contains a valid field reference in this workflow
// Note that an empty string will pass this check.
const validateFieldPathOf =
  (path: string, fieldName = startCase(path), message = '', erroringField = path) =>
  ({
    params,
    context,
    stepContext,
  }: {
    params: StaticWorkflowAction['params'];
    context: WorkflowValidationContext;
    stepContext: StepContext;
  }): ValidationResult => {
    const param = get(path, params);

    // This check does not make assumption about presence. A field is allowed to be null or undefined.
    // Use validatePresence to validate existence of a param.
    if (!param) {
      return Valid;
    }
    const { valid, errorMessages } = fieldPathHasValidSchema(param, stepContext, context);
    return valid
      ? Valid
      : {
          field: erroringField,
          message: message ? message : `${fieldName} has invalid path: ${errorMessages.join(', ')}`,
        };
  };

// Validate all the values in an object are valid field references in this workflow
// Note that an empty object will pass this check.
const validateFieldPathsOf =
  (path: string) =>
  ({
    params,
    context,
    stepContext,
  }: {
    params: StaticWorkflowAction['params'];
    context: WorkflowValidationContext;
    stepContext: StepContext;
  }): ValidationResult => {
    const param = get(path, params);

    // This check does not make assumption about presence. A field is allowed to be null or undefined.
    // Use validatePresence to validate existence of a param.
    if (!param || typeof param !== 'object') {
      return Valid;
    }

    const invalidPaths = Object.entries(param).filter(
      ([_, value]) =>
        value &&
        typeof value === 'string' &&
        !fieldPathHasValidSchema(value, stepContext, context).valid,
    );
    return invalidPaths.length > 0
      ? invalidPaths.map(([key, value]) => {
          return {
            field: `${path}.${key}`,
            message: `${startCase(key)} has invalid path: ${value}`,
          };
        })
      : Valid;
  };

// Validate that the field contains reference to a valid workflow object of a specific types
// This check will pass if the field is empty. Use validatePresenceOf to check the existence of field.
// This check only considers top level tokens from previous workflow steps.
// For checking subfields inside an object, use validateFieldPathOf
const validateTypeOf =
  (path: string, types: WorkflowFieldObjectKey | WorkflowFieldObjectKey[]) =>
  ({
    params,
    stepContext,
  }: {
    params: StaticWorkflowAction['params'];
    stepContext: StepContext;
  }): ValidationResult => {
    const targetToken = get(path, params);
    if (!targetToken) {
      return Valid;
    }

    const filteredObjects = filterUsableObjects({
      usableObjects: stepContext.usableObjects,
      options: {
        permittedObjects: [types].flat(),
        excludeNonPersonaObjects: true,
      },
    });
    const tokenExists = filteredObjects.some((obj) => obj?.value === targetToken);
    return tokenExists
      ? Valid
      : {
          message: `Selected object must be one of ${[types].flat().join(', ')}`,
          field: path,
        };
  };

type FieldMappingKey<T> = Extract<
  Extract<
    keyof {
      [P in keyof T as T[P] extends Record<string, string> ? P : never]: P;
    },
    keyof T
  >,
  string
>;

const validatePresenceOfVerificationTemplateVersionId = validatePresenceOf(
  'verification-template-version-id',
  'Verification template should be selected',
);

const validateObjectFieldPathOfVerification = validateFieldPathOf('object');

const validateObjectsFieldPathsOfVerification = validateFieldPathsOf('objects');

const validatePresenceOfVerification = validatePresenceOf(
  'verification',
  'Verification is required',
);

const validateFieldMappingOf =
  <T>({
    fieldMappingKey,
    displayName,
    pathPrefix,
  }: {
    fieldMappingKey: FieldMappingKey<T>;
    displayName: string;
    pathPrefix?: string;
  }) =>
  ({
    params,
    context,
    stepContext,
  }: {
    params: T;
    context: WorkflowValidationContext;
    stepContext: StepContext;
  }): ValidationResult => {
    if (params[fieldMappingKey] == null || isEmpty(params[fieldMappingKey])) {
      return {
        message: `${displayName} should be configured`,
        field: String(fieldMappingKey),
      };
    } else {
      const errors = Object.entries(params[fieldMappingKey] as Record<string, string>)
        .map(([key, path]) => {
          const fullPath = pathPrefix ? `${pathPrefix}.${path}` : path;
          const { valid, errorMessages } = fieldPathHasValidSchema(fullPath, stepContext, context);
          if (!valid) {
            return {
              message: `${startCase(key)} has invalid path: ${errorMessages.join(', ')}`,
              field: `${String(fieldMappingKey)}.${key}`,
            };
          }
        })
        .filter(Boolean);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return errors.length > 0 ? errors : Valid;
    }
  };

const validationRules: ActionParamsValidators = {
  [WorkflowStepActionKey.AddItemToListFace]: [
    validatePresenceOf('list-id', 'List should be selected'),
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.AddItemToList]: [
    validatePresenceOf('list-id', 'List should be selected'),
    validateFieldPathsOf('list-item-fields'),
    validatePresenceOf('list-item-fields', 'List item fields should be configured'),
  ],
  [WorkflowStepActionKey.RemoveItemFromList]: [
    validatePresenceOf('list-id', 'List should be selected'),
    validateFieldPathsOf('list-item-fields'),
    validatePresenceOf('list-item-fields', 'List item fields should be configured'),
  ],
  [WorkflowStepActionKey.ListMatch]: [
    validatePresenceOf('list-id', 'List should be selected'),
    validateFieldPathsOf('list-item-fields'),
    validatePresenceOf('list-item-fields', 'List item fields should be configured'),
  ],
  [WorkflowStepActionKey.MxCreateUser]: [
    validatePresenceOf('external-integration-id', 'MX API Key must be selected'),
    validatePresenceOf('user-id', 'ID is required'),
    validateHandlebarsOf('user-id', 'ID'),
    validateHandlebarsOf('email', 'Email'),
  ],
  [WorkflowStepActionKey.MxGetMicrodepositStatus]: [
    validatePresenceOf('external-integration-id', 'MX API Key must be selected'),
    validatePresenceOf('user-guid', 'MX User GUID is required'),
    validatePresenceOf('microdeposit-guid', 'MX Micro-Deposit GUID is required'),
    validateHandlebarsOf('user-guid', 'MX User GUID'),
    validateHandlebarsOf('microdeposit-guid', 'MX Micro-Deposit GUID'),
  ],
  [WorkflowStepActionKey.MxRequestConnectWidget]: [
    validatePresenceOf('external-integration-id', 'MX API Key must be selected'),
    validatePresenceOf('user-guid', 'MX User GUID is required'),
    validateHandlebarsOf('user-guid', 'MX User GUID'),
    validateHandlebarsOf('microdeposit-guid', 'MX Micro-Deposit GUID'),
  ],
  [WorkflowStepActionKey.NovaCreditCashAtlas]: [
    validatePresenceOf('input-fields.product-id', 'Product ID is required', 'product-id'),
  ],
  [WorkflowStepActionKey.NovaCreditCreditPassport]: [
    validatePresenceOf('input-fields.product-id', 'Product ID is required', 'product-id'),
  ],
  [WorkflowStepActionKey.Query]: [
    validatePresenceOf('object-type', 'Object type is required'),
    validatePresenceOf('filters', 'At least one filter required'),
  ],
  [WorkflowStepActionKey.TrustDeviceFromInquiry]: [
    validatePresenceOf('inquiry'),
    validateTypeOf('inquiry', WorkflowFieldObjectKey.Inquiry),
  ],
  [WorkflowStepActionKey.RunReportAddressLookup]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportAdverseMedia]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessAdverseMedia]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessAssociatedPersons]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessClassification]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessIndustryClassification]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessLiensLookup]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessLookup]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessNonprofit]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessOnlinePresence]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessRegistrationsLookup]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessSocialMedia]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessWatchlist]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportBusinessWebsite]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportCryptoAddressWatchlist]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportCustomList]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportEmailAddress]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportPhoneNumber]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportPoliticallyExposedPerson]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportProfile]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportProfileNonAuthoritative]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportScreening]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportSocialMedia]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportSynthetic]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.RunReportWatchlist]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [DeprecatedWorkflowStepActionKey.CreateReportAdverseMedia]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [DeprecatedWorkflowStepActionKey.CreateReportEmailAddress]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [DeprecatedWorkflowStepActionKey.CreateReportPhoneNumber]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [DeprecatedWorkflowStepActionKey.CreateReportProfile]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [DeprecatedWorkflowStepActionKey.CreateReportSocialMedia]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [DeprecatedWorkflowStepActionKey.CreateReportSynthetic]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [DeprecatedWorkflowStepActionKey.CreateReportWatchlist]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.CreateCase]: [
    validatePresenceOf('case-template-token', 'Case template should be selected'),
    ({ params, stepContext: { usableObjects } }) => {
      const possibleCaseObjects = caseAttachmentsFor(usableObjects).map(({ value }) => value);
      const invalidObjects = (params['case-objects'] ?? []).filter(
        (input) => !possibleCaseObjects.includes(input),
      );
      return invalidObjects.length > 0
        ? {
            message: `Unusable object is selected: ${invalidObjects.join(', ')}`,
            field: 'case-objects',
          }
        : Valid;
    },
    ({ params, context, step }) => {
      // when passed a graphQuery case object created from a runGraphQuery workflow step,
      // ensure that a snapshot is being generated as well
      const stepsInPath = findPath(step.token, context.stepsConfig);
      const runGraphQuerySteps: WorkflowStepAction[] = stepsInPath.filter(
        (step) =>
          step.type === WorkflowStepType.Action &&
          step.action?.key === WorkflowStepActionKey.RunGraphQuery,
      ) as WorkflowStepAction[];
      const runGraphQueryStepsAttachedToCase = runGraphQuerySteps.filter((step) =>
        (params['case-objects'] || []).includes(step.token),
      );
      const stepsThatDontGenerateSnapshot = runGraphQueryStepsAttachedToCase
        .filter(
          (step) => !(step.action as WorkflowActionRunGraphQueryV1)?.params['generate-snapshot'],
        )
        .map((step) => `'${step.name}'`);

      return stepsThatDontGenerateSnapshot.length > 0
        ? {
            message: `'Run Graph Query' steps must generate a snapshot if attached to a case: ${stepsThatDontGenerateSnapshot.join(
              ', ',
            )}`,
            field: 'case-objects',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.CreateDocumentGovernmentId]: [
    validatePresenceOf('verification-template-id', 'Verification template should be selected'),
    validateFieldMappingOf({
      fieldMappingKey: 'verification-fields',
      displayName: 'Verification fields',
    }),
  ],
  [WorkflowStepActionKey.CreateTransaction]: [
    validatePresenceOf('transaction-type-token', 'Type should be selected'),
    validateFieldPathsOf('transaction-fields'),
  ],
  [DeprecatedWorkflowStepActionKey.ResolveCase]: [
    validatePresenceOf('case'),
    validateTypeOf('case', WorkflowFieldObjectKey.Case),
    validatePresenceOf('resolution'),
  ],
  [WorkflowStepActionKey.SetCaseSla]: [
    validatePresenceOf('case'),
    validateTypeOf('case', WorkflowFieldObjectKey.Case),
    ({ params }) => {
      return params['sla-seconds'] < 1
        ? {
            message: 'Time must be positive.',
            field: 'sla-seconds',
          }
        : Valid;
    },
    ({ params }) => {
      return params['sla-seconds'] > 2678400
        ? {
            message: 'Time cannot be greater than 31 days.',
            field: 'sla-seconds',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.SetRelation]: [
    validatePresenceOf('relation-schema-name'),
    validatePresenceOf('assigned-object'),
    validateTypeOf('object', [WorkflowFieldObjectKey.Transaction, WorkflowFieldObjectKey.Account]),
    validateTypeOf('assigned-object', [
      WorkflowFieldObjectKey.Transaction,
      WorkflowFieldObjectKey.Account,
    ]),
  ],
  [WorkflowStepActionKey.UnsetRelation]: [
    validatePresenceOf('relation-schema-name'),
    validateTypeOf('object', [WorkflowFieldObjectKey.Transaction, WorkflowFieldObjectKey.Account]),
    validateTypeOf('unset-object', [
      WorkflowFieldObjectKey.Transaction,
      WorkflowFieldObjectKey.Account,
    ]),
  ],
  [WorkflowStepActionKey.UpdateCaseStatus]: [
    validatePresenceOf('case'),
    validateTypeOf('case', WorkflowFieldObjectKey.Case),
    validatePresenceOf('state-ids', 'Status should be selected'),
  ],
  [WorkflowStepActionKey.RedactObject]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
    ({ params }) => {
      return !params['redact-all'] && (params['fields'] == null || params['fields']?.length === 0)
        ? {
            message: 'Select "Personally Identifiable Information" or any field to redact',
            field: 'fields',
          }
        : Valid;
    },
    ({ params }) => {
      return params['retention-period-days'] && params['retention-period-days'] < 0
        ? {
            message: 'Retention period must be 0 or greater',
            field: 'retention-period-days',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.TagObject]: [
    validatePresenceOf('tag-id', 'Tag should be selected'),
    ({ params, stepContext: { usableObjects } }) => {
      const taggableObjects = taggableObjectsFor(usableObjects).map(({ value }) => value);
      return taggableObjects.includes(params.object)
        ? Valid
        : {
            message: 'Existing object should be selected',
            field: 'object',
          };
    },
  ],
  [WorkflowStepActionKey.CopyTags]: [
    validatePresenceOf('from-object', 'From field should be selected'),
    validatePresenceOf('to-object', 'To field should be selected'),
    ({ params }) => {
      return params['from-object'] === params['to-object'] && !!params['from-object']
        ? {
            message: 'From and To fields must be different',
            field: 'to-object',
          }
        : Valid;
    },
    ({ params, stepContext: { usableObjects } }) => {
      const taggableObjects = taggableObjectsFor(usableObjects).map(({ value }) => value);
      const errors = [];
      if (!!params['from-object'] && !taggableObjects.includes(params['from-object'])) {
        errors.push({
          message: 'From field must be one of account, case, inquiry, report, or transaction',
          field: 'from-object',
        });
      }
      if (!!params['to-object'] && !taggableObjects.includes(params['to-object'])) {
        errors.push({
          message: 'To field must be one of account, case, inquiry, report, or transaction',
          field: 'to-object',
        });
      }
      return errors.length > 0 ? errors : Valid;
    },
  ],
  [WorkflowStepActionKey.RemoveTag]: [
    validatePresenceOf('tag-ids', 'Tag should be selected'),
    ({ params, stepContext: { usableObjects } }) => {
      const taggableObjects = taggableObjectsFor(usableObjects).map(({ value }) => value);
      return taggableObjects.includes(params.object)
        ? Valid
        : {
            message: 'Existing object should be selected',
            field: 'object',
          };
    },
  ],
  [WorkflowStepActionKey.EvaluateCode]: [
    validatePresenceOf('serialized-code', 'Write code to be evaluated'),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    ({ params, context, stepContext }) => {
      // v1
      if (!('input-args' in params)) {
        return Valid;
      }
      // v2
      const errors = Object.entries(params['input-args'])
        .map(([argKey, argValue]) => {
          const { valid, errorMessages } = fieldPathHasValidSchema(argValue, stepContext, context);
          if (!valid) {
            return {
              message: `${argKey} has invalid path ${argValue}: ${errorMessages.join(', ')}`,
              field: `input-args.${argKey}`,
            };
          }
        })
        .filter(Boolean);
      return errors.length > 0 ? errors : Valid;
    },
    ({ params }) => {
      if (!('timeout' in params)) {
        return Valid;
      }
      if (
        params.timeout == null ||
        (Number.isInteger(params.timeout) && params.timeout >= 1 && params.timeout <= 60)
      ) {
        return Valid;
      } else {
        return {
          message: 'Should be blank or 1-60 in integer',
          field: 'timeout',
        };
      }
    },
  ],
  [WorkflowStepActionKey.SmsInquiryLink]: [
    validatePresenceOf('phone-number', 'Phone number is required'),
    validateHandlebarsOf('phone-number'),
    validatePresenceOf('inquiry', 'Inquiry is required'),
    validateTypeOf('inquiry', [WorkflowFieldObjectKey.Inquiry]),
    ({ params }) => {
      if (params['expires-in-minutes'] == null) return Valid;
      if (params['expires-in-minutes'] > 0) return Valid;

      return {
        message: 'Expires in minutes must be greater than 0',
        field: 'expires-in-minutes',
      };
    },
  ],
  [WorkflowStepActionKey.RunReportWithParams]: [
    validatePresenceOf('report-fields', 'Report fields should be configured'),
    validateFieldPathsOf('report-fields'),
    ({ params, context, stepContext }) => {
      const { valid, errorMessages } = fieldPathHasValidSchema(
        params['report-template-id'],
        stepContext,
        context,
      );
      return params['report-template-id'] && valid
        ? Valid
        : {
            message: `Report template should be selected: ${errorMessages.join(', ')}`,
            field: 'report-template-id',
          };
    },
  ],
  [WorkflowStepActionKey.RunReportGeneric]: [
    validatePresenceOf('report-template-token', 'Report template should be selected'),
    validatePresenceOf('report-fields', 'Report fields should be configured'),
    validateFieldPathsOf('report-fields'),
    validateFieldPathOf('object'),
  ],
  [WorkflowStepActionKey.CreateVerificationDocumentFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
  ],
  [WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
  ],
  [WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
  ],
  [WorkflowStepActionKey.CreateVerificationSelfieFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
  ],
  [WorkflowStepActionKey.CreateVerificationAamvaFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
  ],
  [WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
  ],
  [WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
  ],
  [WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
  ],
  [WorkflowStepActionKey.RunVerificationDatabaseBusiness]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
    validateObjectFieldPathOfVerification,
    validateObjectsFieldPathsOfVerification,
  ],
  [WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
    validateObjectFieldPathOfVerification,
    validateObjectsFieldPathsOfVerification,
  ],
  [WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
    validateObjectFieldPathOfVerification,
    validateObjectsFieldPathsOfVerification,
  ],
  [WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
    validateObjectFieldPathOfVerification,
    validateObjectsFieldPathsOfVerification,
  ],
  [WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
    validateObjectFieldPathOfVerification,
    validateObjectsFieldPathsOfVerification,
  ],
  [WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
    validateObjectFieldPathOfVerification,
    validateObjectsFieldPathsOfVerification,
  ],
  [WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
    validateObjectFieldPathOfVerification,
    validateObjectsFieldPathsOfVerification,
  ],
  [WorkflowStepActionKey.RunVerificationGeneric]: [
    validatePresenceOfVerificationTemplateVersionId,
    validateFieldMappingOf({
      fieldMappingKey: 'verification-fields',
      displayName: 'Verification fields',
    }),
    validateObjectFieldPathOfVerification,
    validateObjectsFieldPathsOfVerification,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    ({ params, context, stepContext }) => {
      if (
        params['verification-meta'] != null &&
        Object.keys(params['verification-meta']).length !== 0 &&
        params['verification-meta']?.['document']?.['comparison-attributes'] != null &&
        Object.keys(params['verification-meta']['document']['comparison-attributes']).length !== 0
      ) {
        const errors = Object.entries(
          params['verification-meta']['document']['comparison-attributes'],
        )
          .map(([key, path]) => {
            const { valid, errorMessages } = fieldPathHasValidSchema(path, stepContext, context);
            if (!valid) {
              return {
                message: `${startCase(key)} has invalid path ${path}: ${errorMessages.join(', ')}`,
                field: `verification-meta.document.comparison-attributes.${key}`,
              };
            }
          })
          .filter(Boolean);
        return errors.length > 0 ? errors : Valid;
      } else {
        return Valid;
      }
    },
    ({ params }) => {
      if (
        params['verification-type'] === ApiModel.VerificationDatabaseTin &&
        Object.keys(params['verification-fields']).includes('name_business') &&
        Object.keys(params['verification-fields']).includes('name_first') &&
        Object.keys(params['verification-fields']).includes('name_last')
      ) {
        return {
          message: "Can't input both name_business and name_first/name_last",
          field: 'verification-fields',
        };
      } else {
        return Valid;
      }
    },
    ({ params }) => {
      if (params['verification-type'] === ApiModel.VerificationDatabaseBusiness) {
        if (
          Object.keys(params['verification-fields']).includes('associated_person_1_name') &&
          (Object.keys(params['verification-fields']).includes('associated_person_1_name_first') ||
            Object.keys(params['verification-fields']).includes('associated_person_1_name_last'))
        ) {
          return {
            message: "Can't input both full name and name_first/name_last for associated person",
            field: 'verification-fields',
          };
        } else if (
          Object.keys(params['verification-fields']).includes('associated_person_2_name') &&
          (Object.keys(params['verification-fields']).includes('associated_person_2_name_first') ||
            Object.keys(params['verification-fields']).includes('associated_person_2_name_last'))
        ) {
          return {
            message: "Can't input both full name and name_first/name_last for associated person",
            field: 'verification-fields',
          };
        } else {
          return Valid;
        }
      } else {
        return Valid;
      }
    },
    ({ params }) => {
      return params['debug-forced-status'] === ''
        ? {
            message: 'Field status override required',
            field: 'input-fields',
          }
        : Valid;
    },
    ({ params, context }) =>
      params.object &&
      context.graph[params.object] == null &&
      !params.object.startsWith(BaseTrigger)
        ? {
            message: 'Existing object should be selected',
            field: 'object',
          }
        : Valid,
  ],
  [WorkflowStepActionKey.ChainalysisAddressScreening]: [
    validatePresenceOf('report-template-token', 'Chainalysis report template must be selected'),
    validatePresenceOf('report-fields.address', 'Address is required'),
    validateHandlebarsOf('report-fields.address'),
  ],
  [WorkflowStepActionKey.ClearbitBusinessLookup]: [
    validatePresenceOf('input-fields.email-address', 'Email address is required', 'email-address'),
  ],
  [WorkflowStepActionKey.CoinbaseCheckCryptoRisk]: [
    validatePresenceOf('input-fields.asset', 'Asset is required', 'asset'),
    validatePresenceOf('input-fields.address', 'Address is required', 'address'),
    validateFieldPathsOf('input-fields'),
  ],
  [WorkflowStepActionKey.EquifaxOneview]: [
    validatePresenceOf('report-template-token', 'Equifax OneView report template must be selected'),
    validatePresenceOf('report-fields.name-first', 'First name is required', 'name-first'),
    validatePresenceOf('report-fields.name-last', 'Last name is required', 'name-last'),
    validatePresenceOf('report-fields.address-city', 'Address City is required'),
    validatePresenceOf('report-fields.address-state', 'Address State is required'),
    ({ params }) => {
      const reportFields = Object.keys(params['report-fields']);

      return reportFields.includes('social-security-number') ||
        reportFields.includes('address-street-1')
        ? Valid
        : {
            message: 'Either SSN or Address Street 1 must be provided',
            field: 'object',
          };
    },
    validateHandlebarsOf('report-fields.name-first'),
    validateHandlebarsOf('report-fields.name-last'),
    validateHandlebarsOf('report-fields.address-city'),
    validateHandlebarsOf('report-fields.address-state'),
  ],
  [WorkflowStepActionKey.MxAccount]: [
    validatePresenceOf('report-template-token', 'MX report template must be selected'),
    validatePresenceOf('input-fields.user-guid', 'User GUID is required', 'user_guid'),
    validatePresenceOf('input-fields.member-guid', 'Member GUID is required', 'member_guid'),

    ({ params }) => {
      const inputFields = Object.keys(params['input-fields']);

      return (inputFields.includes('name-first') && inputFields.includes('name-last')) ||
        inputFields.includes('business-name')
        ? Valid
        : {
            message: 'Either First Name/Last Name or Business Name must be provided',
            field: 'input-fields',
          };
    },

    validateHandlebarsOf('input-fields.user-guid'),
    validateHandlebarsOf('input-fields.member-guid'),
  ],
  [WorkflowStepActionKey.RunKyckrBusinessLookup]: [
    validatePresenceOf(
      'report-template-token',
      'Kyckr Business Lookup report template must be selected',
    ),
    validatePresenceOf('report-fields.country-code', 'Country Code is required', 'country-code'),
    ({ params }) => {
      const reportFields = Object.keys(params['report-fields']);

      return reportFields.includes('company-name') || reportFields.includes('company-number')
        ? Valid
        : {
            message: 'Either Company Name or Company Number must be provided',
            field: 'report-fields',
          };
    },
    validateHandlebarsOf('report-fields.country-code'),
  ],

  [WorkflowStepActionKey.TrmWalletScreening]: [
    validatePresenceOf('report-template-token', 'TRM report template must be selected'),
    validatePresenceOf('report-fields.address', 'Address is required', 'address'),
    validatePresenceOf('report-fields.chain', 'Chain is required', 'chain'),
    validateHandlebarsOf('report-fields.address'),
    validateHandlebarsOf('report-fields.chain'),
  ],
  [WorkflowStepActionKey.SendCaseMessage]: [
    validatePresenceOf('send-message-type', 'Type must be selected'),
    validateTypeOf('case', WorkflowFieldObjectKey.Case),
    validateTypeOf('case-message', WorkflowFieldObjectKey.CaseMessage),
    ({ params }) => {
      return params['send-message-type'] === 'new' && params['case'] == null
        ? {
            message: 'Case should be selected',
            field: 'case',
          }
        : Valid;
    },
    ({ params }) => {
      return params['send-message-type'] === 'reply' && params['case-message'] == null
        ? {
            message: 'Case message should be selected',
            field: 'case-message',
          }
        : Valid;
    },
    validatePresenceOf('to-email-addresses', 'To email address must be entered'),
    validatePresenceOf('case-message-template-token', 'Case message template must be selected'),
  ],
  [WorkflowStepActionKey.SendEmail]: [
    validatePresenceOf('recipient', 'Recipient is required'),
    validatePresenceOf('subject', 'Subject is required'),
    validatePresenceOf('body-html', 'Body (HTML) is required'),
    validatePresenceOf('body-text', 'Body (Text) is required'),
    validateHandlebarsOf('recipient'),
    validateHandlebarsOf('subject'),
    validateHandlebarsOf('body-html', 'Body (HTML)'),
    validateHandlebarsOf('body-text', 'Body (Text)'),
  ],
  [WorkflowStepActionKey.SendSlackMessage]: [
    validatePresenceOf('url', 'URL is required'),
    validatePresenceOf('message', 'Message is required'),
    validateHandlebarsOf('url'),
    validateHandlebarsOf('message'),
    validateHandlebarsOf('override'),
  ],
  [WorkflowStepActionKey.SlackOAuthSendMessage]: [
    validatePresenceOf('external-integration-id', 'Connection must be selected'),
    validatePresenceOf('recipient-type', 'Recipient type must be selected'),
    validatePresenceOf('recipient-id', 'Recipient must be selected'),
    validatePresenceOf('message', 'Message is required'),
    validateHandlebarsOf('message'),
  ],
  [WorkflowStepActionKey.RunReportSentilinkApplicationRisk]: [
    validatePresenceOf('report-template-token', 'Report template must be selected'),
    validatePresenceOf('report-fields.birthdate', 'Birthdate is required'),
    validatePresenceOf('report-fields.user-id', 'User ID is required'),
    validatePresenceOf('report-fields.name-first', 'First name is required'),
    validatePresenceOf('report-fields.name-last', 'Last name is required'),
    validatePresenceOf('report-fields.address-street-1', 'Street address is required'),
    validatePresenceOf('report-fields.address-city', 'City is required'),
    validatePresenceOf('report-fields.address-postal-code', 'Postal code is required'),
    validatePresenceOf('report-fields.address-subdivision', 'Subdivision is required'),
    validatePresenceOf('report-fields.address-country-code', 'Country code is required'),
    validateHandlebarsOf('report-fields.birthdate'),
    validateHandlebarsOf('report-fields.user-id'),
    validateHandlebarsOf('report-fields.name-first'),
    validateHandlebarsOf('report-fields.name-last'),
    validateHandlebarsOf('report-fields.address-street-1'),
    validateHandlebarsOf('report-fields.address-city'),
    validateHandlebarsOf('report-fields.address-postal-code'),
    validateHandlebarsOf('report-fields.address-subdivision'),
    validateHandlebarsOf('report-fields.address-country-code'),
  ],
  [WorkflowStepActionKey.RunReportSentilinkScores]: [
    validatePresenceOf('report-template-token', 'Report template must be selected'),
    validatePresenceOf('report-fields.birthdate', 'Birthdate is required'),
    validatePresenceOf('report-fields.user-id', 'User ID is required'),
    validatePresenceOf('report-fields.name-first', 'First name is required'),
    validatePresenceOf('report-fields.name-last', 'Last name is required'),
    validatePresenceOf('report-fields.address-street-1', 'Street address is required'),
    validatePresenceOf('report-fields.address-city', 'City is required'),
    validatePresenceOf('report-fields.address-postal-code', 'Postal code is required'),
    validatePresenceOf('report-fields.address-subdivision', 'Subdivision is required'),
    validatePresenceOf('report-fields.address-country-code', 'Country code is required'),
    validateHandlebarsOf('report-fields.birthdate'),
    validateHandlebarsOf('report-fields.user-id'),
    validateHandlebarsOf('report-fields.name-first'),
    validateHandlebarsOf('report-fields.name-last'),
    validateHandlebarsOf('report-fields.address-street-1'),
    validateHandlebarsOf('report-fields.address-city'),
    validateHandlebarsOf('report-fields.address-postal-code'),
    validateHandlebarsOf('report-fields.address-subdivision'),
    validateHandlebarsOf('report-fields.address-country-code'),
  ],
  [WorkflowStepActionKey.SubmitMiddeskBusinessLookup]: [
    validatePresenceOf('report-template-token', 'Middesk report template must be selected'),
    validatePresenceOf('report-fields.business-name', 'Business Name is required'),
    validatePresenceOf('report-fields.address-street-1', 'Address Street 1 is required'),
    validatePresenceOf('report-fields.address-city', 'Address City is required'),
    validatePresenceOf('report-fields.address-subdivision', 'Address Subdivision is required'),
    validatePresenceOf('report-fields.address-postal-code', 'Address Postal Code is required'),
    validatePresenceOf('report-fields.address-country-code', 'Address Country Code is required'),
    validateHandlebarsOf('report-fields.business-name'),
    validateHandlebarsOf('report-fields.address-street-1'),
    validateHandlebarsOf('report-fields.address-city'),
    validateHandlebarsOf('report-fields.address-subdivision'),
    validateHandlebarsOf('report-fields.address-postal-code'),
    validateHandlebarsOf('report-fields.address-country-code'),
  ],
  [WorkflowStepActionKey.ZendeskAddTagsToTicket]: [
    validatePresenceOf('external-integration-id', 'Zendesk Instance must be selected'),
    validatePresenceOf('ticket-id', 'Ticket ID is required'),
    validatePresenceOf('tags', 'Tags are required'),
    validateHandlebarsOf('ticket-id', 'Ticket ID'),
  ],
  [WorkflowStepActionKey.ZendeskCreateTicket]: [
    validatePresenceOf('domain-url', 'Domain URL is required'),
    validatePresenceOf('account-email', 'Account Email is required'),
    validatePresenceOf('api-token', 'API Token is required'),
    validatePresenceOf('body', 'Comment Body is required'),
    validateHandlebarsOf('domain-url', 'Domain URL'),
    validateHandlebarsOf('account-email', 'Account Email'),
    validateHandlebarsOf('api-token', 'API Token'),
    validateHandlebarsOf('body', 'Comment Body'),
  ],
  [WorkflowStepActionKey.ZendeskOAuthCreateTicket]: [
    validatePresenceOf('external-integration-id', 'Zendesk Instance must be selected'),
    validatePresenceOf('body', 'Comment Body is required'),
    validateHandlebarsOf('subject'),
    validateHandlebarsOf('body', 'Comment Body'),
  ],
  [WorkflowStepActionKey.ZendeskOAuthUpdateTicket]: [
    validatePresenceOf('external-integration-id', 'Zendesk Instance must be selected'),
    validatePresenceOf('body', 'Comment Body is required'),
    validatePresenceOf('ticket-id', 'Ticket ID is required'),
    validateHandlebarsOf('body', 'Comment Body'),
    validateHandlebarsOf('ticket-id', 'Ticket ID'),
  ],
  [WorkflowStepActionKey.ZendeskRemoveTagsFromTicket]: [
    validatePresenceOf('external-integration-id', 'Zendesk Instance must be selected'),
    validatePresenceOf('ticket-id', 'Ticket ID is required'),
    validatePresenceOf('tags', 'Tags are required'),
    validateHandlebarsOf('ticket-id', 'Ticket ID'),
  ],
  [WorkflowStepActionKey.ZendeskUpdateTicket]: [
    validatePresenceOf('domain-url', 'Domain URL is required'),
    validatePresenceOf('account-email', 'Account Email is required'),
    validatePresenceOf('api-token', 'API Token is required'),
    validatePresenceOf('body', 'Comment Body is required'),
    validatePresenceOf('ticket-id', 'Ticket ID is required'),
    validateHandlebarsOf('domain-url', 'Domain URL'),
    validateHandlebarsOf('account-email', 'Account Email'),
    validateHandlebarsOf('api-token', 'API Token'),
    validateHandlebarsOf('body', 'Comment Body'),
    validateHandlebarsOf('ticket-id', 'Ticket ID'),
  ],
  [WorkflowStepActionKey.HubspotOAuthCreateObject]: [
    validatePresenceOf('external-integration-id', 'Hubspot Instance must be selected'),
    validatePresenceOf('object-type', 'Object Type must be selected'),
    ({ params }) => {
      return !params['object-properties'] || params['object-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('object-properties'),
    validateResolvedJsonOf('object-properties'),
    validateHandlebarsOf('associated-object-id', 'Associated Object ID'),
  ],
  [WorkflowStepActionKey.HubspotOAuthSearchObject]: [
    validatePresenceOf('external-integration-id', 'Hubspot Instance must be selected'),
    validatePresenceOf('object-type', 'Object Type must be selected'),

    ({ params }) => {
      return !params['object-properties'] || params['object-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('object-properties'),
    validateResolvedJsonOf('object-properties'),
  ],
  [WorkflowStepActionKey.HubspotOAuthUpdateObject]: [
    validatePresenceOf('external-integration-id', 'Hubspot Instance must be selected'),
    validatePresenceOf('object-type', 'Object Type must be selected'),
    validatePresenceOf('object-id', 'Object ID is required'),

    ({ params }) => {
      return !params['object-properties'] || params['object-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('associated-object-id', 'Associated Object ID'),
    validateHandlebarsOf('object-id', 'Object ID'),
    validateHandlebarsOf('object-properties'),
    validateResolvedJsonOf('object-properties'),
  ],
  [WorkflowStepActionKey.HubspotUpdateObject]: [
    validatePresenceOf('access-token', 'Access Token is required'),
    validatePresenceOf('object-type', 'Object Type is required'),
    validatePresenceOf('object-id', 'Object ID is required'),
    validateHandlebarsOf('access-token', 'Access Token'),
    validateHandlebarsOf('object-type', 'Object Type'),
    validateHandlebarsOf('object-id', 'Object ID'),
    ({ params }) => {
      return !params['object-properties'] || params['object-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('object-properties'),
    validateResolvedJsonOf('object-properties'),
  ],
  [WorkflowStepActionKey.SalesforceCreateObject]: [
    validatePresenceOf('domain-url', 'Domain URL is required'),
    validatePresenceOf('client-id', 'Client ID is required'),
    validatePresenceOf('client-secret', 'Client Secret is required'),
    validatePresenceOf('username', 'Username is required'),
    validatePresenceOf('password', 'Password is required'),
    validatePresenceOf('object-type', 'Object Type is required'),
    validateHandlebarsOf('domain-url', 'Domain URL'),
    validateHandlebarsOf('client-id', 'Client ID'),
    validateHandlebarsOf('client-secret', 'Client Secret'),
    validateHandlebarsOf('username', 'Username'),
    validateHandlebarsOf('password', 'Password'),
    validateHandlebarsOf('object-type', 'Object Type'),
    ({ params }) => {
      return !params['object-properties'] || params['object-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('object-properties'),
    validateResolvedJsonOf('object-properties'),
  ],
  [WorkflowStepActionKey.SalesforceOAuthCreateRecord]: [
    validatePresenceOf('external-integration-id', 'Salesforce instance must be selected'),
    validatePresenceOf('record-type', 'Record Type must be selected'),
    ({ params }) => {
      return !params['record-properties'] || params['record-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('record-properties'),
    validateResolvedJsonOf('record-properties'),
  ],
  [WorkflowStepActionKey.SalesforceOAuthSearchRecord]: [
    validatePresenceOf('external-integration-id', 'Salesforce instance must be selected'),
    validatePresenceOf('record-type', 'Record Type must be selected'),
    ({ params }) => {
      return !params['record-properties'] || params['record-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('record-properties'),
    validateResolvedJsonOf('record-properties'),
  ],
  [WorkflowStepActionKey.SalesforceOAuthUpdateRecord]: [
    validatePresenceOf('external-integration-id', 'Salesforce instance must be selected'),
    validatePresenceOf('record-id', 'Record ID is required'),
    validatePresenceOf('record-type', 'Record Type must be selected'),
    ({ params }) => {
      return !params['record-properties'] || params['record-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('record-id'),
    validateHandlebarsOf('record-properties'),
    validateResolvedJsonOf('record-properties'),
  ],
  [WorkflowStepActionKey.SalesforceUpdateObject]: [
    validatePresenceOf('domain-url', 'Domain URL is required'),
    validatePresenceOf('client-id', 'Client ID is required'),
    validatePresenceOf('client-secret', 'Client Secret is required'),
    validatePresenceOf('username', 'Username is required'),
    validatePresenceOf('password', 'Password is required'),
    validatePresenceOf('object-type', 'Object Type is required'),
    validatePresenceOf('object-id', 'Object ID is required'),
    validateHandlebarsOf('domain-url', 'Domain URL'),
    validateHandlebarsOf('client-id', 'Client ID'),
    validateHandlebarsOf('client-secret', 'Client Secret'),
    validateHandlebarsOf('username', 'Username'),
    validateHandlebarsOf('password', 'Password'),
    validateHandlebarsOf('object-type', 'Object Type'),
    validateHandlebarsOf('object-id', 'Object ID'),
    ({ params }) => {
      return !params['object-properties'] || params['object-properties'] === '{}'
        ? {
            message: 'At least one Properties key value pair is required',
            field: 'object-properties',
          }
        : Valid;
    },
    validateHandlebarsOf('object-properties'),
    validateResolvedJsonOf('object-properties'),
  ],
  [WorkflowStepActionKey.GPTGenerateMessage]: [
    validatePresenceOf('external-integration-id', 'OpenAI API key must be selected'),
    validatePresenceOf('gpt-model', 'GPT-Model key must be selected'),
    validatePresenceOf('prompt', 'Prompt is required'),
    validateHandlebarsOf('prompt', 'Prompt'),
  ],
  [WorkflowStepActionKey.HttpRequest]: [
    validatePresenceOf('url', 'URL is required'),
    validateHandlebarsOf('url'),
    validatePresenceOf('method', 'HTTP method should be selected'),
    ({ params }) => {
      if (params.method === 'GET' && params['return-schema']) {
        const jsonSchema =
          JSON.parse(params['return-schema'].config['json-schema'])?.['properties'] ?? {};
        if (Object.keys(jsonSchema).length === 0) {
          return {
            message: 'GET request should have return schema',
            field: 'return-schema',
          };
        }
      }
      return Valid;
    },
    validateResolvedJsonOf('body'),
    ({ params }) => {
      return params.timeout == null || (params.timeout >= 1 && params.timeout <= 60)
        ? Valid
        : {
            message: 'Request timeout should be between 1 and 60 seconds.',
            field: 'timeout',
          };
    },
    ({ params }) => {
      return !params['headers-v2'] ||
        params['headers-v2'].every((header) => header.key && header.value)
        ? Valid
        : {
            message: 'All headers must have key and value.',
            field: 'headers-v2',
          };
    },
  ],
  [WorkflowStepActionKey.LabelObject]: [
    validatePresenceOf('object'),
    validateTypeOf('object', WorkflowFieldObjectKey.Transaction),
  ],
  [WorkflowStepActionKey.CreateInquiry]: [
    validatePresenceOf('template-id', 'Template should be selected'),
    validateTypeOf('account', WorkflowFieldObjectKey.Account),
    validateFieldPathsOf('inquiry-fields'),
    ({ params }) => {
      return params['account'] == null
        ? {
            message: 'Account should be selected',
            field: 'account',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDocument]: [
    validatePresenceOfVerification,
  ],
  [WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabase]: [
    validatePresenceOfVerification,
    ({ params }) => {
      return !params['inquiry-to-verification-field-mapping'] == null ||
        isEmpty(params['inquiry-to-verification-field-mapping'])
        ? {
            message: 'Field mapping should be configured',
            field: 'inquiry-to-verification-field-mapping',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabaseStandard]: [
    validatePresenceOfVerification,
    ({ params }) => {
      return !params['inquiry-to-verification-field-mapping'] == null ||
        isEmpty(params['inquiry-to-verification-field-mapping'])
        ? {
            message: 'Field mapping should be configured',
            field: 'inquiry-to-verification-field-mapping',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationGovernmentId]: [
    validatePresenceOfVerification,
    ({ params }) => {
      return !params['inquiry-to-verification-field-mapping'] == null ||
        isEmpty(params['inquiry-to-verification-field-mapping'])
        ? {
            message: 'Field mapping should be configured',
            field: 'inquiry-to-verification-field-mapping',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.UpdateInquiryFromPersona]: [],
  [WorkflowStepActionKey.UpdateAccountFieldsFromInquiry]: [
    validateFieldMappingOf({
      fieldMappingKey: 'account-to-inquiry-field-mapping',
      displayName: 'Field mapping',
      pathPrefix: 'trigger.fields',
    }),
    ({ params }) => {
      // this list is from app/lib/workflow_action/update_account_fields_from_inquiry_v1.rb:30
      const ALLOWED_ACCOUNT_FIELDS = [
        'name-first',
        'name-middle',
        'name-last',
        'country-code',
        'address-street-1',
        'address-street-2',
        'address-subdivision',
        'address-city',
        'address-postal-code',
        'identification-class',
        'identification-number',
        'birthdate',
        'phone-number',
        'email-address',
      ];

      const invalidFields = remove(
        (field) => ALLOWED_ACCOUNT_FIELDS.includes(field),
        Object.keys(params['account-to-inquiry-field-mapping'] ?? {}),
      );

      const errors = invalidFields.map((invalidField) => {
        return {
          message: `"${invalidField}" is invalid in this mapping`,
          field: `account-to-inquiry-field-mapping.${invalidField}`,
        };
      });

      return errors.length > 0 ? errors : Valid;
    },
  ],
  [WorkflowStepActionKey.UpdateAccountFromUserInput]: [
    validatePresenceOf('account'),
    ({ params }) => {
      return !params['account-fields'] == null || isEmpty(params['account-fields'])
        ? {
            message: 'Account fields should be configured',
            field: 'account-fields',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.UpdateObject]: [
    validatePresenceOf('object'),
    validateFieldPathOf('object'),
    ({ params }) => {
      return isEmpty(params.fields) &&
        isEmpty(params.values) &&
        (params.operators == null ||
          Object.values(params.operators).every((value) => value !== Operator.Unset))
        ? {
            message: 'Object fields should be configured',
            field: 'fields',
          }
        : Valid;
    },
    ({ params, context, stepContext }) => {
      const errors: PartialWorkflowError[] = [];

      flattenKeyValue(params.fields).forEach(([key, value]) => {
        const fieldName = `fields.${key}`;
        const stringValue = value as string;
        if (!isValidHandlebarsText(stringValue)) {
          errors.push({
            field: fieldName,
            message: `${fieldName} is invalid as text with Handlebars`,
          });
        } else {
          const errorMessages = getHandlebars(stringValue).flatMap(
            (handlebar) => fieldPathHasValidSchema(handlebar, stepContext, context).errorMessages,
          );

          if (errorMessages.length > 0) {
            errors.push({
              field: fieldName,
              message: `${fieldName} has invalid handlebars: ${errorMessages.join(', ')}`,
            });
          }
        }
      });
      return errors;
    },
  ],
  [WorkflowStepActionKey.Calculate]: [
    validatePresenceOf('variables', 'Variables should not be empty'),
    validatePresenceOf('return-variables', 'Return variable should not be empty'),
    ({ params, context, stepContext }) => {
      const errors: ValidationResult = [];
      const names: Record<string, boolean> = {};
      params['variables'].forEach(({ id, name, type, value }) => {
        if (!name) {
          errors.push({
            message: 'Name is required',
            field: `variables.${id}-name`,
          });
        } else if (names[name]) {
          errors.push({
            message: 'Name must be unique',
            field: `variables.${id}-name`,
          });
        } else {
          names[name] = true;
        }

        const { operator, valueLeftConfig, valueRightConfig, valueDefaultConfig } =
          getExpressionRuleParts(value);
        if (!VARIABLE_TYPE_TO_OPERATORS[type].includes(operator)) {
          errors.push({
            message: 'Operator is invalid',
            field: `variables.${id}-operator`,
          });
        }

        if (!valueLeftConfig) {
          errors.push({
            message: 'Value is required',
            field: `variables.${id}-left`,
          });
        } else if (
          valueLeftConfig.type === RuleValueTypes.Field &&
          !fieldPathHasValidSchema(valueLeftConfig.value.var, stepContext, context).valid
        ) {
          errors.push({
            message: 'Field is invalid',
            field: `variables.${id}-left`,
          });
        }

        if (!SINGLE_OPERAND_OPERATORS.includes(operator)) {
          if (!valueRightConfig) {
            errors.push({
              message: 'Value is required',
              field: `variables.${id}-right`,
            });
          } else if (
            valueRightConfig.type === RuleValueTypes.Field &&
            !fieldPathHasValidSchema(valueRightConfig.value.var, stepContext, context).valid
          ) {
            errors.push({
              message: 'Field is invalid',
              field: `variables.${id}-right`,
            });
          }
        }

        if (
          valueDefaultConfig &&
          valueDefaultConfig.type === RuleValueTypes.Field &&
          !fieldPathHasValidSchema(valueDefaultConfig.value.var, stepContext, context).valid
        ) {
          errors.push({
            message: 'Field is invalid',
            field: `variables.${id}-default`,
          });
        }
      });
      return errors;
    },
  ],
  [WorkflowStepActionKey.RunGraphQuery]: [
    validatePresenceOf('graph-query-template-id', 'Graph Query Template should be selected'),
    ({ params }) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const errors: ValidationResult = Object.entries(params['query-inputs'])
        .map(([key, value]) => {
          if (value == null) {
            return {
              message: `${startCase(key)} must have a value specified`,
              field: `query-inputs`,
            };
          }
        })
        .filter(Boolean);
      return errors;
    },
    ({ params, context, step }) => {
      // for certain triggers, the runGraphQuery step must be preceded by a SyncGraph Step that either
      // does not selectively sync, or selectively syncs off of the gqt the runGraphQuery step is using

      // sync only applies to inquiry and account/transaction.created events
      const { trigger } = context.inputStep;
      if (trigger?.type !== ApiModel.TriggerEvent) {
        return Valid;
      }
      const eventName = trigger.attributes['event-name'];
      if (
        EVENT_NAME_TO_EVENT_TYPE[eventName] !== EventType.Inquiry &&
        ![EventName.AccountCreated, EventName.TransactionCreated].includes(eventName)
      ) {
        return Valid;
      }

      // don't bother checking if no gqt is selected. There already exists validation above that
      // checks that the graph query template id exists
      if (!params['graph-query-template-id']) {
        return Valid;
      }
      const stepsInPath = findPath(step.token, context.stepsConfig);
      const syncGraphSteps: WorkflowStepAction[] = stepsInPath.filter(
        (step) =>
          step.type === WorkflowStepType.Action &&
          step.action?.key === WorkflowStepActionKey.SyncGraph,
      ) as WorkflowStepAction[];

      const gqtSynced = syncGraphSteps.find((syncGraphStep) => {
        const syncGraphParams = (syncGraphStep.action as WorkflowActionSyncGraphV1).params;
        return (
          !syncGraphParams['use-selective-sync'] ||
          (syncGraphParams['selective-sync-graph-query-template-ids'] || []).includes(
            params['graph-query-template-id'],
          )
        );
      });

      return gqtSynced
        ? Valid
        : {
            message: "Graph Query Template must be synced in a prior 'Sync Graph' step.",
            field: 'graph-query-template-id',
          };
    },
  ],
  [WorkflowStepActionKey.ScheduleWorkflowRun]: [
    validatePresenceOf('workflow-id', 'Workflow should be selected'),
    validatePresenceOf('time-unit', 'Time unit is required'),
    validateFieldPathOf('object'),
    ({ params }) => {
      return params['time-offset'] == null
        ? {
            message: 'Time offset is required',
            field: 'time-offset',
          }
        : Valid;
    },
    ({ params }) => {
      const refdt = params['input-fields']['reference-datetime'];
      return refdt != null && refdt.trim() === ''
        ? {
            message: 'Reference date time required',
            field: 'input-fields',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.SendConfirmationCode]: [
    validatePresenceOf('verification', 'Verification should be selected'),
    validateTypeOf('verification', [
      WorkflowFieldObjectKey.VerificationPhoneNumber,
      WorkflowFieldObjectKey.VerificationEmailAddress,
    ]),
  ],
  [WorkflowStepActionKey.AttemptConfirmationCode]: [
    validatePresenceOf('verification', 'Verification should be selected'),
    validateTypeOf('verification', [
      WorkflowFieldObjectKey.VerificationPhoneNumber,
      WorkflowFieldObjectKey.VerificationEmailAddress,
    ]),
    validatePresenceOf('confirmation-code', 'Confirmation code is required'),
  ],
  [DeprecatedWorkflowStepActionKey.GetAnalyticsMetric]: [
    validatePresenceOf('metric', 'Metric should be selected'),
    ({ params }) => {
      return !Object.values(Metric).includes(params['metric'] as Metric)
        ? {
            message: 'Invalid metric',
            field: 'metric',
          }
        : Valid;
    },
    ({ params }) => {
      const groupBy = params['group-by'];
      return groupBy != null && !Object.values(GroupBy).includes(groupBy as GroupBy)
        ? {
            message: 'Invalid group by',
            field: 'group-by',
          }
        : Valid;
    },
  ],
  [WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress]: [],
  [WorkflowStepActionKey.ConfirmLatestVerificationPhoneNumber]: [],
  [WorkflowStepActionKey.FindOrCreateAccount]: [],
  [WorkflowStepActionKey.ProcessLatestVerificationCertificateKorea]: [],
  [WorkflowStepActionKey.ProcessLatestVerificationDatabaseAadhaar]: [],
  [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdFinnishTrustNetwork]: [],
  [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdOneId]: [],
  [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdSingpass]: [],
  [WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationEmailAddress]: [],
  [WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationPhoneNumber]: [],
  [WorkflowStepActionKey.UpdateAccount]: [],
  [WorkflowStepActionKey.UpdateInquiryNextStepName]: [],
  [WorkflowStepActionKey.UpdateInquiryStateDatabaseError]: [],
  [WorkflowStepActionKey.UpdateInquiryStateDocumentError]: [],
  [WorkflowStepActionKey.UpdateInquiryStateGovernmentIdError]: [],
  [WorkflowStepActionKey.UpdateInquiryStateSelfieError]: [],
  [DeprecatedWorkflowStepActionKey.UpdateInquiryFieldsFromVerification]: [],
};

export const validateActionStep = (
  step: WorkflowStepAction,
  validationContext: WorkflowValidationContext,
): WorkflowConfigError[] => {
  // Newly created actions do not have an action
  if (step.action.key === NullWorkflowStepActionKey.NullKey) {
    return [
      {
        token: step.token,
        message: 'This action is invalid',
        type: WorkflowErrorType.Save,
      },
    ];
  }

  const stepContext = validationContext.getContextForStep(step.token);
  return (validationRules[(step.action as StaticWorkflowAction).key] || []).flatMap((validator) => {
    const result = validator({
      params: step.action.params as never, // Unfortunate any, TS doesn't understand validator is scoped by step.action.key
      context: validationContext,
      stepContext,
      step,
    });
    if (result === Valid) {
      return [];
    } else {
      return (Array.isArray(result) ? result : [result]).map((partialError) => ({
        ...partialError,
        token: step.token,
        type: WorkflowErrorType.Publish,
      }));
    }
  });
};
